import { useCallback, useMemo } from "react"
import fp from "lodash/fp"

import type {
  DeviceRowsByInstallationStatus,
  InstalledOnLoading,
} from "types/device.types"

import useDevices from "helpers/hooks/useDevices"
import useOrgDeviceStatuses from "helpers/hooks/devices/useOrgDeviceStatuses"
import { useAllPowerlines } from "helpers/hooks/powerlines/useAllPowerlines"
import { useAllTowers } from "helpers/hooks/towers/useAllTowers"
import {
  formatDevicesWithInstalledOnById,
  formatDevicesWithStatus,
} from "helpers/utils/devices"
import { getTowerDevicesFromRow } from "helpers/utils/towers"
import { getPowerlineAndTowerDevicesFromRow } from "helpers/utils/powerlines"

export const useFullDeviceRows = () => {
  const {
    devsWRelsByIdWStates,
    devicesByInstallationStatus,
    isFetching: devicesFetching,
    isLoading: devicesLoading,
    isSuccess: devicesSuccess,
  } = useDevices()
  const { statusesById, isLoading: statusesLoading } = useOrgDeviceStatuses()
  const { powerlinesById, isLoading: powerlinesLoading } = useAllPowerlines()
  const { towersById, isLoading: towersLoading } = useAllTowers()

  const devicesWithStatusById = useMemo(
    () => formatDevicesWithStatus(devsWRelsByIdWStates, statusesById),
    [statusesById, devsWRelsByIdWStates],
  )

  const devicesRowsById = useMemo(
    () =>
      formatDevicesWithInstalledOnById(devicesWithStatusById, towersById, powerlinesById),
    [devicesWithStatusById, towersById, powerlinesById],
  )

  const devicesRowsList = useMemo(() => Object.values(devicesRowsById), [devicesRowsById])

  const devicesRowsByInstallationStatus = useMemo(
    (): DeviceRowsByInstallationStatus => ({
      installed: fp.flow(
        fp.keys,
        fp.map((deviceId: string) => devicesRowsById[deviceId]),
        fp.compact,
      )(devicesByInstallationStatus.installed),
      notInstalled: fp.flow(
        fp.keys,
        fp.map((deviceId: string) => devicesRowsById[deviceId]),
        fp.compact,
      )(devicesByInstallationStatus.notInstalled),
      all: devicesRowsList,
    }),
    [devicesByInstallationStatus, devicesRowsById, devicesRowsList],
  )

  const getTowerDevicesRows = useCallback(
    (towerId: number): DeviceRowsByInstallationStatus => ({
      installed: [],
      notInstalled: [],
      all: getTowerDevicesFromRow(devicesRowsList, towerId),
    }),
    [devicesRowsList],
  )

  const getPowerlineDevicesRows = useCallback(
    (powerlineId: number, towerIds: Set<number>): DeviceRowsByInstallationStatus => ({
      installed: [],
      notInstalled: [],
      all: getPowerlineAndTowerDevicesFromRow(devicesRowsList, powerlineId, towerIds),
    }),
    [devicesRowsList],
  )

  const getGroupDevicesRows = useCallback(
    (deviceList: number[]): DeviceRowsByInstallationStatus => ({
      installed: [],
      notInstalled: [],
      all: fp.compact(deviceList.map((id) => devicesRowsById[String(id)])),
    }),
    [devicesRowsById],
  )

  const installedOnLoading: InstalledOnLoading = {
    towers: towersLoading,
    powerlines: powerlinesLoading,
  }

  const isFirstFetch = useMemo(
    () => devicesFetching && fp.isEmpty(devsWRelsByIdWStates),
    [devsWRelsByIdWStates, devicesFetching],
  )

  return {
    devicesRowsByInstallationStatus,
    getTowerDevicesRows,
    getPowerlineDevicesRows,
    getGroupDevicesRows,
    installedOnLoading,
    statusesLoading,
    towersLoading,
    isFetching: devicesFetching,
    isLoading: devicesLoading,
    isSuccess: devicesSuccess,
    isFirstFetch,
  }
}
